<script setup lang="ts">
type ButtonStyle = 'default' | 'outlined';

type Props = {
  disabled?: boolean;
  buttonStyle?: ButtonStyle;
};

const { disabled: isDisabled, buttonStyle = 'default' } = defineProps<Props>();

type Emits = {
  click: [];
};

const emit = defineEmits<Emits>();

const isButtonStyleDefault = computed<boolean>(() => {
  return buttonStyle === 'default';
});

const isButtonStyleOutlined = computed<boolean>(() => {
  return buttonStyle === 'outlined';
});

function onButtonClick() {
  emit('click');
}
</script>

<template>
  <button
    :class="[
      $style.button,
      {
        [$style.isDisabled]: isDisabled,
        [$style.isDefault]: isButtonStyleDefault,
        [$style.isOutlined]: isButtonStyleOutlined,
      },
    ]"
    :disabled="isDisabled"
    @click="onButtonClick"
  >
    <slot />
  </button>
</template>

<style module lang="postcss">
.button {
  @apply flex items-center justify-center gap-2 py-2 px-3 text-base font-medium whitespace-nowrap border border-solid rounded;
  @apply text-primary;

  --darken30: color-mix(in srgb, currentColor 30%, black);

  &.isDefault {
    @apply border-transparent bg-button-bg text-button-text;
  }

  &.isOutlined {
    @apply border-current text-current;

    &:hover,
    &.active,
    &:active,
    &:focus,
    &[aria-selected='true'] {
      @apply outline-none;

      background-color: var(--darken30);
    }
  }

  &.isDisabled {
    @apply bg-opacity-50 cursor-not-allowed text-button-text py-2 px-3 rounded text-base font-medium whitespace-nowrap flex items-center justify-center gap-2;
  }
}
</style>
